import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import PartnerAuth from './components/PartnerAuth';
import PartnerSidebar from './components/PartnerSidebar';
import DashboardOverview from './components/DashboardOverview';
import ProductCatalog from './components/ProductCatalog';
import PricingManagement from './components/PricingManagement';
import ScanAnalytics from './components/ScanAnalytics';
import Recommendations from './components/Recommendations';
import PartnerSettings from './components/PartnerSettings';
import { ArrowRight, X } from 'lucide-react';

export default function RetailPartnerPortal() {
  const navigate = useNavigate();
  const [partnerUser, setPartnerUser] = useState(null);
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [authLoading, setAuthLoading] = useState(true);
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (session?.user) {
          const { data: profile } = await supabase
            ?.from('partner_profiles')
            ?.select('*')
            ?.eq('id', session?.user?.id)
            ?.maybeSingle();
          if (profile) {
            // Redirect to onboarding wizard if not completed
            if (profile?.onboarding_completed === false) {
              navigate('/partner-onboarding-wizard', { replace: true });
              return;
            }
            setPartnerUser(session?.user);
            setPartnerProfile(profile);
          }
        }
      } catch (e) {
        console.error('Session check error:', e);
      } finally {
        setAuthLoading(false);
      }
    };
    checkSession();
  }, []);

  const handleAuthSuccess = (user, profile, needsOnboarding = false) => {
    if (needsOnboarding) {
      navigate('/partner-onboarding-wizard', { replace: true });
      return;
    }
    setPartnerUser(user);
    setPartnerProfile(profile);
    // Show banner if onboarding not completed (came back via save & continue later)
    if (profile?.onboarding_completed === false) {
      setShowOnboardingBanner(true);
    }
  };

  const handleSignOut = async () => {
    await supabase?.auth?.signOut();
    setPartnerUser(null);
    setPartnerProfile(null);
  };

  const handleProfileUpdate = (updated) => {
    setPartnerProfile(updated);
  };

  const stepsRemaining = partnerProfile ? (
    4 - (partnerProfile?.onboarding_step ?? 1) + 1
  ) : 0;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0D1B2A' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#00E5FF', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Loading portal...</p>
        </div>
      </div>
    );
  }

  if (!partnerUser || !partnerProfile) {
    return <PartnerAuth onAuthSuccess={handleAuthSuccess} />;
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard': return <DashboardOverview partnerId={partnerUser?.id} />;
      case 'catalog': return <ProductCatalog partnerId={partnerUser?.id} />;
      case 'pricing': return <PricingManagement partnerId={partnerUser?.id} />;
      case 'analytics': return <ScanAnalytics partnerId={partnerUser?.id} />;
      case 'recommendations': return <Recommendations partnerId={partnerUser?.id} />;
      case 'settings': return <PartnerSettings partnerProfile={partnerProfile} onProfileUpdate={handleProfileUpdate} />;
      default: return <DashboardOverview partnerId={partnerUser?.id} />;
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#0D1B2A', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-4" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.15), transparent 70%)' }} />
        <div className="absolute bottom-0 left-64 w-96 h-96 rounded-full opacity-4" style={{ background: 'radial-gradient(circle, rgba(123,47,190,0.15), transparent 70%)' }} />
      </div>

      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full z-30">
        <PartnerSidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          partnerProfile={partnerProfile}
          onSignOut={handleSignOut}
        />
      </div>

      {/* Main Content */}
      <main className="flex-1 ml-64 min-h-screen relative z-10">
        {/* Onboarding Banner */}
        {showOnboardingBanner && partnerProfile?.onboarding_completed === false && (
          <div
            className="flex items-center justify-between px-6 py-3"
            style={{
              background: 'linear-gradient(90deg, rgba(0,229,255,0.12), rgba(123,47,190,0.12))',
              borderBottom: '1px solid rgba(0,229,255,0.2)',
            }}
          >
            <p className="text-sm font-medium" style={{ color: '#E2E8F0', fontFamily: 'DM Sans, sans-serif' }}>
              ✨ Complete your partner setup to unlock all features —{' '}
              <span style={{ color: '#00E5FF' }}>{Math.max(stepsRemaining, 1)} step{stepsRemaining !== 1 ? 's' : ''} remaining</span>
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/partner-onboarding-wizard')}
                className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg"
                style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', color: '#fff', fontFamily: 'DM Sans, sans-serif' }}
              >
                Continue Setup <ArrowRight size={13} />
              </button>
              <button onClick={() => setShowOnboardingBanner(false)} style={{ color: '#64748B' }}>
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        <div className="max-w-6xl mx-auto px-6 py-8">
          {renderSection()}
        </div>
      </main>
    </div>
  );
}
